import { useContext, useState, useCallback } from 'react'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import {
  TextField,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { motion } from 'framer-motion'
import { useSelector, useDispatch } from 'react-redux'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'
import { CvsAvatar, Paper } from '@components'
import { userContext } from '@contexts'
import { colors } from '@global/constants'
import { useAppSelector } from '@redux/hooks'
import { ProfileApiKey } from './generateApiKey'
import * as AuthActions from '../authed/auth.actions'
import { AuthScheme } from '@global/api/types'
import { useAuth0 } from '@auth0/auth0-react'
import { useAuthHintQuery, useSsoEnabled } from '@global/hooks/queries/useAuthHintQuery'

const useStyles = makeStyles(theme => ({
  holder: {
    height: '100%',
    overflow: 'scroll',
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
  },
  root: {
    height: '380px',
    padding: theme.spacing(3),
    paddingLeft: '126px',
    paddingTop: '24px',
    marginTop: theme.spacing(3),
  },
  textBoxes: {
    marginTop: '8px',
    marginBottom: '4px',
  },
  paper: {
    marginTop: theme.spacing(3),
    paddingTop: '24px',
  },
  avatarOverlayContainer: {
    cursor: 'pointer',
    position: 'relative',
    display: 'inlineBlock',
    borderRadius: '125px',
    overflow: 'hidden',
  },
  avatarOverlay: {
    background: 'rgba(0, 0, 0, 0.4)',
    color: 'white',
    position: 'absolute',
    bottom: '0',
    left: '0',
    right: '0',
    height: '50px',
    paddingTop: '15px',
    fontWeight: '600',
    cursor: 'pointer',
  },
}))

export const Profile = props => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const user = useSelector(state => state.Auth.user)
  const [title, setTitle] = useState(user.title || '')
  const [location, setLocation] = useState(user.location || '')
  const { updateAvatar, selectAndImportPhoto, updateUserFields } = useContext(userContext)
  const [showWelcomeModal, setShowWelcomeModal] = useState(
    props.location.search.includes('welcome'),
  )

  const { data: authHint } = useAuthHintQuery()
  const ssoEnabled = useSsoEnabled()
  const { logout: auth0Logout } = useAuth0()
  const [SSO_LOGOUT = false] = useAppSelector(selectFeatureIncludes([FeatureDict.SSO_LOGOUT]))

  const hasApi = useSelector(state => state.Auth.features['public-api-keys'])

  const changeAvatar = () => {
    selectAndImportPhoto('avatar').then(formData => updateAvatar(formData))
  }

  const handleSave = useCallback(() => {
    const profileFields = {
      name: user.name,
      title,
      location,
      email: user.email,
      id: user.id,
    }

    updateUserFields(profileFields)
  }, [updateUserFields])

  const logout = () => {
    if (ssoEnabled && SSO_LOGOUT) {
      window.location.href = '/logout'
      return
    }

    if (authHint?.auth_scheme === AuthScheme.dsc_universal_login) {
      void auth0Logout({ logoutParams: { federated: true, returnTo: window.location.origin } })
      return
    }

    dispatch(AuthActions.logout())
  }

  return (
    <motion.div
      className={classes.holder}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.15 } }}
      exit={{ opacity: 0, transition: { duration: 0.15 } }}
      style={{ padding: 24 }}
    >
      <div style={{ padding: 24, paddingTop: 8, flexShrink: 1 }}>
        <Typography variant="h3">{t`Profile settings`}</Typography>
      </div>
      <Paper elevation={3} className={classes.paper}>
        <div style={{ display: 'flex' }}>
          <div style={{ display: 'flex', flex: 1, justifyContent: 'flex-end', paddingRight: 12 }}>
            <a href="https://support.claravine.com" target="_blank" rel="noreferrer">
              <Button>{t`Support`}</Button>
            </a>
            {(!ssoEnabled || SSO_LOGOUT) && (
              <>
                <div style={{ width: 1, backgroundColor: colors.blue.smokey }} />
                <Button onClick={logout}>{t`Logout`}</Button>
              </>
            )}
          </div>
        </div>
        <div
          className={classes.root}
          style={{ display: 'flex', maxWidth: '1000px', margin: 'auto', textAlign: 'center' }}
        >
          <div style={{ margin: '75px 50px' }}>
            <div onClick={changeAvatar} className={classes.avatarOverlayContainer}>
              <CvsAvatar
                user={user}
                size={150}
                innerStyle={{
                  margin: '5px, 5px, 0px',
                  cursor: 'pointer',
                }}
              />
              <div className={classes.avatarOverlay}>{t`Change`}</div>
            </div>
            <Typography variant="h4" style={{ paddingTop: '5px' }}>
              {user.name}
            </Typography>
          </div>
          <div style={{ width: '45%' }}>
            <div
              style={{
                padding: '10px',
                maxWidth: '400px',
                height: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 15,
              }}
            >
              <TextField
                className={classes.textBoxes}
                style={{ backgroundColor: colors.gray.light }}
                fullWidth
                id="outlined-basic"
                label={t`Name`}
                variant="outlined"
                value={user.name}
              />
              <TextField
                className={classes.textBoxes}
                style={{ backgroundColor: colors.gray.light }}
                fullWidth
                id="outlined-basic"
                label={t`Email`}
                variant="outlined"
                value={user.email}
              />
              <TextField
                className={classes.textBoxes}
                fullWidth
                id="outlined-basic"
                label={t`Title`}
                variant="outlined"
                onChange={({ target: { value } }) => setTitle(value)}
                value={title}
              />
              <TextField
                className={classes.textBoxes}
                fullWidth
                id="outlined-basic"
                label={t`Location`}
                variant="outlined"
                onChange={({ target: { value } }) => setLocation(value)}
                value={location}
              />
            </div>
            <Button
              style={{
                marginTop: '12px',
                color: 'white',
                height: '30px',
              }}
              color="secondary"
              variant="contained"
              onClick={handleSave}
            >
              {t`Save`}
            </Button>
          </div>
        </div>
      </Paper>
      {hasApi && <ProfileApiKey />}
      <Dialog
        onClose={() => {
          setShowWelcomeModal(false)
        }}
        aria-labelledby="customized-dialog-title"
        open={showWelcomeModal}
      >
        <DialogTitle
          id="customized-dialog-title"
          onClose={() => {
            setShowWelcomeModal(false)
          }}
        >
          {t`Welcome to the new Claravine user interface!`}
        </DialogTitle>
        <DialogContent dividers>
          <Typography gutterBottom>
            <Trans>
              We're excited to finally let you try out the new UI. We've spent countless hours
              updating the look and feel of the application, as well as adding usability and feature
              enhancements, to provide you with a better experience. Our users are at the heart of
              everything we do, so we'd love to hear any feedback you have, or how we can do better.
            </Trans>
          </Typography>
          <Typography gutterBottom>
            <Trans>
              Please note that this is still your live production account and any changes or
              submissions made here will be visible to all users even if they are using the classic
              interface. Remember, we're in early access so please let us know about any bumps or
              hiccups you encounter. Enjoy!
            </Trans>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClose={() => {
              setShowWelcomeModal(false)
            }}
            onClick={() => {
              setShowWelcomeModal(false)
            }}
            color="primary"
          >
            {t`Get started`}
          </Button>
        </DialogActions>
      </Dialog>
    </motion.div>
  )
}
